/**
 * CDN Loader Hook
 *
 * @param {'sandbox' | 'development' | 'qa' | 'production'} environment - the current runtime env (if you have different environment names use a function to mapp them
 *
 * @example
 * const { loadScript, doesScriptExist } = useCDNLoader('development');
 *
 * loadScript(
 *   'https://cdn.example.com/library.js',
 *   'example-library',
 *   ['development', 'qa', 'production'],
 *   true
 * )
 *
 * doesScriptExist('example-library'); // returns true or false
 *
 * @example
 * // Usage using a environment mapping function
 * const mapEnvironment = (env: string): 'development' | 'qa' | 'production' => {
 *   switch(env) {
 *     case 'sandbox':
 *       return 'development';
 *     case 'staging':
 *       return 'qa';
 *     case 'prod':
 *       return 'production';
 *     default:
 *       return 'development';
 *   }
 * };
 * const currentEnv = mapEnvironment(import.meta.env.MODE);
 * const { loadScript, doesScriptExist } = useCDNLoader(currentEnv);
 */
function useCDNLoader(environment) {
    const doesScriptExist = (id) => {
        return document.body.querySelector(`#${id}`) !== null;
    };
    const loadScript = (src, id, enableIn, deferLoading = true) => {
        return new Promise((resolve, reject) => {
            if (doesScriptExist(id)) {
                const existingScript = document.getElementById(id);
                existingScript?.remove();
            }
            if (enableIn.includes(environment)) {
                if (environment !== 'production') {
                    console.log(`Loading ${id} cdn script...`);
                }
                const script = document.createElement('script');
                script.setAttribute('src', src);
                script.setAttribute('id', id);
                script.setAttribute('type', 'text/javascript');
                document.body.insertAdjacentElement('beforeend', script);
                if (deferLoading)
                    script.setAttribute('defer', '');
                script.onload = () => {
                    if (environment !== 'production') {
                        // Only log in non-production environments
                        console.log(`${id} cdn script loaded.`);
                    }
                    resolve();
                };
                script.onerror = (event) => {
                    console.error(`Error loading script with src ${src}.`, event);
                    reject(new Error(`Failed to load script with src ${src}.`));
                };
            }
        });
    };
    return { loadScript, doesScriptExist };
}

export { useCDNLoader };
